import { ChildAgesFormData } from '@/types/benefits/hotel/HotelFormData';
import {
  ArrayPath,
  Control,
  FieldArray,
  Path,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import StyledInput from '../styled/StyledInput';
import StyledButton from '../styled/StyledButton';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { Label } from '../ui/label';
import { useTranslation } from '@/i18n';

interface ChildAgeDefinitionProps<FieldsType extends ChildAgesFormData> {
  control: Control<FieldsType>;
  label?: string;
  isViewMode?: boolean;
}

const ChildAgeDefinition = <FieldsType extends ChildAgesFormData>({
  control,
  label: labelProp,
  isViewMode = false,
}: ChildAgeDefinitionProps<FieldsType>) => {
  const { t } = useTranslation();

  const label = labelProp ?? t('hotels.childrenAcceptedIf');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'childAges' as ArrayPath<FieldsType>,
  });

  const firstChildAgeLimit = useWatch({
    control,
    name: 'childAges.0.childAgeTo' as Path<FieldsType>,
  });

  return (
    <>
      <div className="flex flex-col w-full gap-1.5">
        <Label className="text-gray-700">{label}</Label>
        {fields.map((field, index) => (
          <div className="flex justify-between" key={field.id}>
            <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
              <Label className="text-gray-700">{t('hotels.from')}</Label>
              <div className="w-16">
                <StyledInput
                  control={control}
                  type="number"
                  name={`childAges.${index}.childAgeFrom` as Path<FieldsType>}
                  min={0}
                  disabled={isViewMode}
                />
              </div>
              <Label className="text-gray-700">{t('hotels.yearsTo')}</Label>
              <div className="w-16">
                <StyledInput
                  control={control}
                  type="number"
                  name={`childAges.${index}.childAgeTo` as Path<FieldsType>}
                  min={1}
                  disabled={isViewMode}
                />
              </div>
              <Label className="text-gray-700">{t('hotels.included')}</Label>
            </div>
            {index === 1 && !isViewMode && (
              <StyledButton
                title={t('common.delete')}
                variant="link"
                onClick={() => remove(index)}
              />
            )}
          </div>
        ))}
        {fields.length === 1 && !isViewMode && (
          <StyledButton
            title={t('hotels.addAgeRange')}
            variant="link"
            className="text-brand-600 w-fit p-0"
            icon={<PlusIcon className="size-5 fill-brand-600" />}
            onClick={() =>
              append({
                childAgeFrom: +(firstChildAgeLimit ?? 11) + 1,
                childAgeTo:
                  firstChildAgeLimit && +firstChildAgeLimit > 17
                    ? +firstChildAgeLimit + 1
                    : 17,
              } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][number])
            }
          />
        )}
      </div>
    </>
  );
};

export default ChildAgeDefinition;
